import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Adds a recommended refill from the cart drawer without leaving it.
 *
 * The refill is posted to the Ajax Cart API with every cart section's ID, then a
 * CartLinesUpdateEvent carries the rendered sections so `cart-items-component`
 * morphs the drawer (lines, subtotal, checkout) and `cart-icon` updates its count,
 * the same contract `product-form.js` uses. The event is dispatched only after a
 * successful add, so a failure leaves the card and its error message in place.
 */
class CartRefillRecommendation extends Component {
  /** Blocks repeat submissions while a refill is being added. */
  #busy = false;

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const form = event.target;
    if (!(form instanceof HTMLFormElement) || this.#busy) return;

    const button = form.querySelector('button[type="submit"]');
    const buttonText = form.querySelector('.cart-refill__button-text');
    const status = form.querySelector('[data-refill-status]');
    const variantId = new FormData(form).get('id');
    if (!(button instanceof HTMLButtonElement) || typeof variantId !== 'string') return;

    const defaultText = buttonText?.textContent ?? '';
    this.#busy = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (buttonText) buttonText.textContent = this.dataset.addingText ?? defaultText;
    if (status) status.textContent = '';

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Add to cart failed');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
      const cart = await cartResponse.json();

      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: 1,
          source: 'cart-refill-recommendation',
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      if (status) status.textContent = this.dataset.errorText ?? '';
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Add to cart failed',
          code: 'INVALID',
        })
      );
    } finally {
      this.#busy = false;
      // On success the drawer re-render removes this card; reset it in case it stays.
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (buttonText) buttonText.textContent = defaultText;
      }
    }
  }
}

if (!customElements.get('cart-refill-recommendation')) {
  customElements.define('cart-refill-recommendation', CartRefillRecommendation);
}
